/**
 * 권리사 「할 일」 — 검수 답하기 · 화이트리스트 처리를 회사 전체로 모은다 (2026-10-21 · 시안 docs/design/rights-todo-inbox/).
 *
 * ## 왜
 * 권리사는 검수 신청에 답하고, 채널 단위 작품이면 사용 승인된 채널을 유튜브에서 화이트리스트로 처리해야 한다.
 * 지금까지 두 일 모두 **작품을 하나씩 열어야** 보였다 → 누락(2026-10-21 실DB: 화리 대기 채널 181곳 · 가장 오래된 건 2025-11).
 * 이 파일의 숫자를 상단바 「검수/화리」 배지 · 작품 탭 「오늘 할 일」 카드 · 검수/화리 탭 「할 일 전체」가 **같이** 쓴다.
 *
 * ## 무엇을 세나 (판정은 이 파일 한 곳)
 *  - 검수 답하기 = `video_inspection.status` 가 검수 신청·재검수(`PENDING_INSPECTION_STATUSES`) — 크리에이터가 고치는 중(수정 요청)은 권리사 몫이 아니다.
 *  - 화이트리스트 처리
 *     · 채널 단위 작품(`licensed_video.whitelist_scope='channel'`): 사용 승인(`channel_to_video.status=true` · 반려 아님)됐는데
 *       `whitelist_bool` 이 참이 아닌 채널. **삭제된 채널(`channel.deleted_at`)은 뺀다** — 걸 대상이 없다(실DB 14곳).
 *       참여 채널 내려받기 RPC `export_channel_video_whitelist` 도 삭제 채널을 뺀다.
 *     · 영상 단위 작품(`'video'`): 화리 요청(`whitelist_requested`)이 있고 미처리이며 신청 취소가 아닌 검수 건(`isVideoWhitelistPending`).
 *     · 처리 안 함(빈 값): 대상 없음.
 *  - 저작권 신고(차단 알림, 2026-10-21 추가) = 열린 신고(`isOpenBlock`)가 있는 **작품 × 채널 한 줄**(창 `groupReportsByChannel` 과 같은 단위).
 *    숫자에는 **차단 신고(reported)가 하나라도 있는 줄만** 센다. 「차단 의심」(suspect)만 있는 줄은 시스템이 감지해 크리에이터 답을 기다리는 상태라
 *    권리사가 할 일이 아니다(가이드 r-r-review 「크리에이터 답을 기다리면 됩니다」) — `block.waiting` 으로 따로 보여 주고 세지 않는다.
 *    세면 권리사가 숫자를 없애려고 「확인했어요」를 눌러 크리에이터에게 쓸데없는 알림이 간다(리뷰 2026-10-21).
 *    작품 화면 [차단 알림] 빨간 숫자도 이 값(`byWork[id].block`)을 쓴다.
 *
 * 모든 함수는 순수 — 읽기는 `rightsTodoApi.js`, 화면 공유는 `contexts/RightsTodoContext.jsx`.
 */
import { PENDING_INSPECTION_STATUSES, isApprovedWorkChannel, isVideoWhitelistPending } from "./workPerformance";
import { isOpenBlock } from "./blockReport";

export { isVideoWhitelistPending };

/** 「내일 자동 승인」으로 강조하는 남은 날 — 이 이하이면 급함(기한 지남 포함). `inspectionProgress.daysLeft` 와 같은 올림 계산 */
export const URGENT_DAYS = 1;

const DAY_MS = 86400000;

/** 검수 답할 건인가 */
export function isOpenInspection(row) {
  return PENDING_INSPECTION_STATUSES.includes(row?.status);
}

/** 채널 단위 화리 대기 채널인가(채널 행의 삭제 여부는 호출부가 channel 표로 본다) */
export function isChannelWhitelistPending(link) {
  return isApprovedWorkChannel(link) && link?.whitelist_bool !== true;
}

/** ISO → 한국 날짜 「yyyy-MM-dd」 */
function kstDay(value) {
  const t = value ? new Date(value).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  return new Date(t + 9 * 3600 * 1000).toISOString().slice(0, 10);
}

/** 한국 날짜 기준 며칠째 기다렸나 — 오늘 = 0, 어제 = 1. 날짜를 모르면 null */
export function waitedDays(iso, now = Date.now()) {
  const from = kstDay(iso);
  const to = kstDay(now);
  if (!from || !to) return null;
  return Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS));
}

/** 답변 기한까지 남은 날(올림) — `inspectionProgress.daysLeft` 와 같은 식. 기한 없음 → null */
export function daysToDue(respondBy, now = Date.now()) {
  const t = respondBy ? new Date(respondBy).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  return Math.ceil((t - now) / DAY_MS);
}

/** 검수 답할 건 정렬 — 답변 기한이 빠른 순(기한 없는 건은 뒤), 같으면 먼저 들어온 순 */
export function compareByDue(a, b) {
  const ta = a?.respond_by ? new Date(a.respond_by).getTime() : NaN;
  const tb = b?.respond_by ? new Date(b.respond_by).getTime() : NaN;
  const va = Number.isFinite(ta) ? ta : Infinity;
  const vb = Number.isFinite(tb) ? tb : Infinity;
  if (va !== vb) return va - vb;
  const ca = new Date(a?.created_at || 0).getTime() || 0;
  const cb = new Date(b?.created_at || 0).getTime() || 0;
  if (ca !== cb) return ca - cb;
  return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
}

/** 화리 대기 정렬 — 오래 기다린 순(먼저 신청한 순). 신청 시각을 모르는 줄은 뒤로(1970년으로 맨 앞에 오지 않게) */
function compareByWaited(a, b) {
  const ra = a?.appliedAt ? new Date(a.appliedAt).getTime() : NaN;
  const rb = b?.appliedAt ? new Date(b.appliedAt).getTime() : NaN;
  const ta = Number.isFinite(ra) ? ra : Infinity;
  const tb = Number.isFinite(rb) ? rb : Infinity;
  if (ta !== tb) return ta < tb ? -1 : 1;
  return String(a?.key ?? "").localeCompare(String(b?.key ?? ""));
}

const emptyWork = (work, id) => ({
  workId: id,
  title: work?.title || "",
  thumbnail: work?.thumbnail || null,
  scope: work?.whitelist_scope || "",
  inspection: 0,
  resubmit: 0,
  earliestDue: null,
  whitelist: 0,
  oldestAppliedAt: null,
  block: 0,
  oldestBlockAt: null,
});

/**
 * 재료 → 할 일 요약.
 * @param {object} p
 * @param {Array} p.works            회사 작품 `licensed_video` (id, title, thumbnail, whitelist_scope)
 * @param {Array} p.openInspections  답할 검수 건 `video_inspection` (status 가 열린 건 — 다른 상태가 섞여도 여기서 거른다)
 * @param {Array} p.videoWhitelistRows 영상 단위 화리 후보 검수 건 (whitelist_requested · whitelist_processed · status)
 * @param {Array} p.channelLinks     채널 단위 작품의 `channel_to_video` (id, video_id, channel_id, status, rejected_bool, whitelist_bool, created_at)
 * @param {Array} p.channels         위 링크들의 `channel` (id, channel_title, channel_url, channel_id, deleted_at)
 * @param {Array} p.blockReports     회사의 열린 저작권 신고 `inspection_block_report` (id, video_id, channel_id, channel_name, status, kind, reported_at, detected_at, created_at)
 * @param {number|Date} [p.now]
 */
export function buildRightsTodo({ works = [], openInspections = [], videoWhitelistRows = [], channelLinks = [], channels = [], blockReports = [], now = Date.now() } = {}) {
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const workById = new Map((works || []).map((w) => [String(w.id), w]));
  const scopeOf = (id) => workById.get(String(id))?.whitelist_scope || "";
  const byWork = new Map();
  const workEntry = (id, fallbackTitle) => {
    const key = String(id);
    if (!byWork.has(key)) {
      const e = emptyWork(workById.get(key), key);
      if (!e.title && fallbackTitle) e.title = fallbackTitle;
      byWork.set(key, e);
    }
    return byWork.get(key);
  };

  // 1) 검수 답하기
  const inspRows = (openInspections || []).filter(isOpenInspection).slice().sort(compareByDue);
  const today = kstDay(nowMs);
  let urgent = 0;
  let todayNew = 0;
  inspRows.forEach((r) => {
    const e = workEntry(r.video_id, r.video_title);
    e.inspection += 1;
    if (r.status === "resubmit_requested") e.resubmit += 1;
    if (r.respond_by && (!e.earliestDue || new Date(r.respond_by) < new Date(e.earliestDue))) e.earliestDue = r.respond_by;
    const d = daysToDue(r.respond_by, nowMs);
    if (d !== null && d <= URGENT_DAYS) urgent += 1;
    if (kstDay(r.created_at) === today) todayNew += 1;
  });

  // 2) 화이트리스트 — 채널 단위
  const channelById = new Map((channels || []).map((c) => [String(c.id), c]));
  const channelRows = [];
  (channelLinks || []).forEach((l) => {
    if (scopeOf(l.video_id) !== "channel") return;
    if (!isChannelWhitelistPending(l)) return;
    const ch = channelById.get(String(l.channel_id));
    if (!ch || ch.deleted_at) return; // 삭제된 채널은 걸 대상이 없다
    const work = workById.get(String(l.video_id));
    channelRows.push({
      key: `c:${l.id}`,
      kind: "channel",
      ctvId: l.id,
      workId: String(l.video_id),
      workTitle: work?.title || "",
      channelId: String(l.channel_id),
      channelTitle: ch.channel_title || "",
      channelUrl: ch.channel_url || "",
      channelYoutubeId: ch.channel_id || "",
      appliedAt: l.created_at || null,
      waitedDays: waitedDays(l.created_at, nowMs),
    });
  });

  // 3) 화이트리스트 — 영상 단위
  const videoRows = [];
  (videoWhitelistRows || []).forEach((r) => {
    if (scopeOf(r.video_id) !== "video") return;
    if (!isVideoWhitelistPending(r)) return;
    const work = workById.get(String(r.video_id));
    videoRows.push({
      key: `v:${r.id}`,
      kind: "video",
      inspection: r,
      workId: String(r.video_id),
      workTitle: work?.title || r.video_title || "",
      channelId: r.channel_id != null ? String(r.channel_id) : "",
      channelTitle: r.channel_name || "",
      appliedAt: r.created_at || null,
      waitedDays: waitedDays(r.created_at, nowMs),
    });
  });

  const wlRows = [...channelRows, ...videoRows].sort(compareByWaited);
  wlRows.forEach((r) => {
    const e = workEntry(r.workId, r.workTitle);
    e.whitelist += 1;
    if (r.appliedAt && (!e.oldestAppliedAt || new Date(r.appliedAt) < new Date(e.oldestAppliedAt))) e.oldestAppliedAt = r.appliedAt;
  });
  // 가장 오래 기다린 건 — 신청 시각을 아는 줄 중에서(모르는 줄 하나 때문에 칩이 사라지지 않게)
  const oldest = wlRows.find((r) => r.appliedAt && Number.isFinite(new Date(r.appliedAt).getTime()))?.appliedAt || null;

  // 4) 저작권 신고(차단 알림) — 작품 × 채널 한 줄. 알린 시각 = 차단 신고 시각 → 감지 시각 → 만든 시각
  const blockAt = (r) => r.reported_at || r.detected_at || r.created_at || null;
  const blockGroups = new Map();
  (blockReports || []).forEach((r) => {
    if (!isOpenBlock(r) || !r.video_id || !r.channel_id) return;
    const key = `b:${r.video_id}:${r.channel_id}`;
    if (!blockGroups.has(key)) {
      const work = workById.get(String(r.video_id));
      blockGroups.set(key, {
        key,
        workId: String(r.video_id),
        workTitle: work?.title || r.work_title || "",
        channelId: String(r.channel_id),
        channelTitle: r.channel_name || "",
        kinds: [],
        reports: 0,
        onlySuspect: true,
        firstAt: null,
      });
    }
    const g = blockGroups.get(key);
    g.reports += 1;
    if (r.status !== "suspect") g.onlySuspect = false;
    if (r.kind && !g.kinds.includes(r.kind)) g.kinds.push(r.kind);
    if (!g.channelTitle && r.channel_name) g.channelTitle = r.channel_name;
    const at = blockAt(r);
    if (at && Number.isFinite(new Date(at).getTime()) && (!g.firstAt || new Date(at) < new Date(g.firstAt))) g.firstAt = at;
  });
  const blockAll = [...blockGroups.values()]
    .map((g) => ({ ...g, appliedAt: g.firstAt, waitedDays: waitedDays(g.firstAt, nowMs) }))
    .sort(compareByWaited);
  const blockRows = blockAll.filter((g) => !g.onlySuspect); // 권리사가 확인할 줄(세는 줄)
  const blockWaiting = blockAll.filter((g) => g.onlySuspect); // 크리에이터 답 대기(세지 않음)
  blockRows.forEach((g) => {
    const e = workEntry(g.workId, g.workTitle);
    e.block += 1;
    if (g.firstAt && (!e.oldestBlockAt || new Date(g.firstAt) < new Date(e.oldestBlockAt))) e.oldestBlockAt = g.firstAt;
  });
  const oldestBlock = blockRows.find((g) => g.firstAt)?.firstAt || null;

  const list = [...byWork.values()];
  // 작품 순서: 가장 이른 답변 기한 → 같으면 건수 많은 순 → 작품 id
  const dueMs = (w) => {
    const t = w.earliestDue ? new Date(w.earliestDue).getTime() : NaN;
    return Number.isFinite(t) ? t : Infinity;
  };
  const inspectionWorks = list
    .filter((w) => w.inspection > 0)
    .sort((a, b) => (dueMs(a) !== dueMs(b) ? (dueMs(a) < dueMs(b) ? -1 : 1) : b.inspection - a.inspection || a.workId.localeCompare(b.workId)));
  const whitelistWorks = list.filter((w) => w.whitelist > 0).sort((a, b) => compareByWaited({ appliedAt: a.oldestAppliedAt, key: a.workId }, { appliedAt: b.oldestAppliedAt, key: b.workId }));
  const blockWorks = list.filter((w) => w.block > 0).sort((a, b) => compareByWaited({ appliedAt: a.oldestBlockAt, key: a.workId }, { appliedAt: b.oldestBlockAt, key: b.workId }));

  return {
    total: inspRows.length + wlRows.length + blockRows.length,
    inspection: { total: inspRows.length, urgent, todayNew, rows: inspRows, works: inspectionWorks },
    whitelist: {
      total: wlRows.length,
      oldestAt: oldest,
      oldestDays: waitedDays(oldest, nowMs),
      rows: wlRows,
      works: whitelistWorks,
    },
    /** 저작권 신고(차단 알림) — 열린 신고가 있는 작품 × 채널 한 줄씩. 처리는 작품 화면 [차단 알림] 창(BlockReportModal) */
    block: {
      total: blockRows.length,
      oldestAt: oldestBlock,
      oldestDays: waitedDays(oldestBlock, nowMs),
      rows: blockRows,
      works: blockWorks,
      /** 「차단 의심」만 있는 줄 — 크리에이터 답 대기, 숫자에 안 셈 */
      waiting: blockWaiting,
    },
    /** 작품 id(문자열) → { inspection, whitelist, … } — 작품 목록 배지 */
    byWork: Object.fromEntries(list.map((w) => [w.workId, w])),
  };
}

/**
 * 화리 대기 채널 → 참여 채널 내려받기 행 모양(`export_channel_video_whitelist` 와 같은 칸) — `whitelistExport.whitelistExportRows` 에 그대로 넘긴다.
 * 영상 단위 행은 채널을 통째로 거는 일이 아니라 빠진다.
 */
export function whitelistTodoExportSource(rows, works = []) {
  const codeOf = new Map((works || []).map((w) => [String(w.id), w.identification_code || ""]));
  return (rows || [])
    .filter((r) => r?.kind === "channel")
    .map((r) => ({
      channel_title: r.channelTitle,
      channel_url: r.channelUrl,
      channel_id: r.channelYoutubeId,
      video_title: r.workTitle,
      identification_code: codeOf.get(String(r.workId)) || "",
      applied_at: r.appliedAt,
      whitelist_bool: false,
    }));
}

/** 할 일 요약을 한 작품으로 좁힌다 — 「오늘 할 일」 카드에서 작품을 눌러 들어온 경우(`?by=todo&work=<id>`). id 가 없으면 그대로 */
export function filterTodoRowsByWork(rows, workId) {
  if (!workId) return rows || [];
  return (rows || []).filter((r) => String(r?.workId ?? r?.video_id) === String(workId));
}

/** 할 일이 없는 빈 요약 — 읽기 전·실패 때 */
export const EMPTY_RIGHTS_TODO = buildRightsTodo();

/** 상단바·메뉴 배지 숫자 표기 — 0 이면 null(숨김), 100 이상은 「99+」 */
export function todoBadgeText(n) {
  const v = Number(n) || 0;
  if (v <= 0) return null;
  return v > 99 ? "99+" : String(v);
}

/** 검수/화리 탭 「할 일 전체」 주소 */
export const RIGHTS_TODO_PATH = "/video/inspection-manage?by=todo";
